import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, finalize } from 'rxjs';
import {
  LEARNING_BLOCK_CASE_LIMITS,
  LearningBlockCase,
  LearningBlockCasePolarity,
  LearningBlockCaseUpdate,
  LearningBlockCasesService,
  learningBlockCaseErrorText,
  learningBlockComponentLabel,
} from '../../../services/learning-block-cases.service';

/** Comma-separated input → trimmed, de-duplicated anchors (case-insensitive). */
export function parseLearningBlockAnchors(text: string | null | undefined): string[] {
  const seen = new Set<string>();
  const anchors: string[] = [];
  for (const part of String(text ?? '').split(',')) {
    const anchor = part.trim().replace(/\s+/g, ' ');
    const key = anchor.toLocaleLowerCase();
    if (!anchor || seen.has(key)) continue;
    seen.add(key);
    anchors.push(anchor);
  }
  return anchors;
}

/** Same rule as `Validators.required`, but blank text counts as empty — the
 *  backend strips it. The error key stays `required`, so
 *  RequiredFieldFeedbackDirective paints the field (CLAUDE.md rule 5). */
function requiredText(control: AbstractControl): ValidationErrors | null {
  return String(control.value ?? '').trim() ? null : { required: true };
}

/** Backend caps for anchors: at most 8, each at most 60 characters. */
function anchorsLimits(control: AbstractControl): ValidationErrors | null {
  const anchors = parseLearningBlockAnchors(control.value);
  const errors: ValidationErrors = {};
  if (anchors.length > LEARNING_BLOCK_CASE_LIMITS.anchorsMax) {
    errors['tooManyAnchors'] = true;
  }
  if (anchors.some((a) => a.length > LEARNING_BLOCK_CASE_LIMITS.anchorMax)) {
    errors['anchorTooLong'] = true;
  }
  return Object.keys(errors).length ? errors : null;
}

/**
 * Create or edit one learning case (super_admin, platform-wide).
 *
 * Saves itself and closes with the stored case (`undefined` = cancelled or
 * nothing changed). An edit sends only the fields that changed — the PATCH
 * takes any subset. Required-field feedback follows CLAUDE.md rules 5 and 16:
 * the save button stays clickable, submit marks everything touched, the
 * global Enter handling jumps to what is missing.
 */
@Component({
  selector: 'app-learning-block-case-dialog',
  templateUrl: './learning-block-case-dialog.component.html',
  styleUrl: './learning-block-case-dialog.component.scss',
})
export class LearningBlockCaseDialogComponent implements OnInit, OnDestroy {
  /** Set when editing; status changes run through the table actions. */
  @Input() learningCase?: LearningBlockCase;
  /** Allowed component ids (backend registry). */
  @Input() components: string[] = [];

  readonly limits = LEARNING_BLOCK_CASE_LIMITS;

  form!: FormGroup;
  saving = false;
  componentOptions: { id: string; label: string }[] = [];
  /** Parsed from the anchors field on every change (not a template getter). */
  anchors: string[] = [];

  private anchorsSub?: Subscription;

  constructor(
    protected dialogRef: NbDialogRef<LearningBlockCaseDialogComponent>,
    private fb: FormBuilder,
    private casesService: LearningBlockCasesService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  get isEdit(): boolean {
    return !!this.learningCase;
  }

  ngOnInit(): void {
    const c = this.learningCase;
    const ids = [...this.components];
    // A case whose component left the registry stays readable; saving it
    // unchanged does not send the component again.
    if (c && !ids.includes(c.component)) {
      ids.unshift(c.component);
    }
    this.componentOptions = ids.map((id) => ({ id, label: learningBlockComponentLabel(this.translate, id) }));

    this.form = this.fb.group({
      component: [c?.component ?? null, Validators.required],
      polarity: [c?.polarity ?? 'use', Validators.required],
      situation: [c?.situation ?? '', [requiredText, Validators.maxLength(this.limits.textMax)]],
      action: [c?.action ?? '', [requiredText, Validators.maxLength(this.limits.textMax)]],
      anchors: [(c?.anchors ?? []).join(', '), anchorsLimits],
      valid_until: [c?.valid_until ?? null],
      status: ['active', Validators.required],
    });

    this.anchors = parseLearningBlockAnchors(this.form.value.anchors);
    this.anchorsSub = this.form.get('anchors')!.valueChanges.subscribe((value) => {
      this.anchors = parseLearningBlockAnchors(value);
    });
  }

  ngOnDestroy(): void {
    this.anchorsSub?.unsubscribe();
  }

  length(name: 'situation' | 'action'): number {
    return String(this.form.get(name)?.value ?? '').length;
  }

  hasError(name: string, error: string): boolean {
    const control = this.form.get(name);
    return !!control && control.hasError(error) && (control.touched || control.dirty);
  }

  onSubmit(): void {
    if (this.saving) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const values = {
      component: v.component as string,
      situation: String(v.situation).trim(),
      action: String(v.action).trim(),
      anchors: parseLearningBlockAnchors(v.anchors),
      polarity: v.polarity as LearningBlockCasePolarity,
      valid_until: (v.valid_until || null) as string | null,
    };

    let request$: Observable<LearningBlockCase>;
    if (this.learningCase) {
      const changes = this.changedFields(this.learningCase, values);
      if (!Object.keys(changes).length) {
        this.dialogRef.close();
        return;
      }
      request$ = this.casesService.updateCase(this.learningCase.id, changes);
    } else {
      request$ = this.casesService.createCase({ ...values, status: v.status });
    }

    this.saving = true;
    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (saved) => this.dialogRef.close(saved),
      error: (err) =>
        this.toastr.danger(
          learningBlockCaseErrorText(err) || this.translate.instant('learningBlockCases.dialog.saveError'),
          ''
        ),
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private changedFields(
    original: LearningBlockCase,
    next: Required<LearningBlockCaseUpdate>
  ): LearningBlockCaseUpdate {
    const changes: LearningBlockCaseUpdate = {};
    if (next.component !== original.component) changes.component = next.component;
    if (next.situation !== original.situation) changes.situation = next.situation;
    if (next.action !== original.action) changes.action = next.action;
    if (next.polarity !== original.polarity) changes.polarity = next.polarity;
    // Explicit null clears the date.
    if ((next.valid_until || null) !== (original.valid_until || null)) changes.valid_until = next.valid_until;
    const before = original.anchors ?? [];
    if (next.anchors.length !== before.length || next.anchors.some((a, i) => a !== before[i])) {
      changes.anchors = next.anchors;
    }
    return changes;
  }
}
